<script setup lang="ts">
  /**
   * Primis video player unit. The vendor tag renders the player where the
   * script element sits, so it goes into `playerHostEl`, not the document body.
   * Must not be served inside a SafeFrame.
   */
  import { markFirstAdRequest } from '../../utils/consent/firstAdRequest'
  import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
  import useAdFunctions from '../../composables/ad-ops/useAdFunctions'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import { getIndexOfPlacement } from '../../utils/ad-ops/adFunctions'
  import debug from '../../utils/console'
  import { watchAdVisibility } from '../../utils/ad-ops/firstAdVisible'

  const logger = debug.withTag('Primis')

  const PRIMIS_LIVE_VIEW_URL = 'https://live.primis.tech/live/liveView.php'

  export interface PrimisProps {
    placement: string
    /** Id Primis announces the player under; also labels its revenue in AY */
    playerApiId?: string
    /** Primis player id, the `s` param */
    siteId: string
    /** Caps the player so a unit smaller than its card stays centred in it */
    playerMaxWidth?: string
    tilePosition?: number
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    adInstanceId?: string
  }

  const props = withDefaults(defineProps<PrimisProps>(), {
    playerApiId: '',
    playerMaxWidth: '540px',
    tilePosition: -1,
    lazyRender: false,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    adInstanceId: '',
  })

  const { renderWhenCurrentElementIsVisible } = useAdFunctions()
  const { track } = useAnalytics()

  const wrapperEl = useTemplateRef<HTMLElement | null>('wrapperEl')
  const playerHostEl = useTemplateRef<HTMLElement | null>('playerHostEl')
  const placementClassName = ref<string>(props.placement)

  let scriptEl: HTMLScriptElement | null = null

  function getEventData(): NtpAdEvent {
    return {
      ntp_ad_provider: 'primis',
      ntp_ad_brand: 'primis',
      ntp_creative_id: props.siteId,
      ntp_tile_position: props.tilePosition,
      ntp_placement_name: props.placement,
      ntp_ad_refresh: false,
    }
  }

  function buildPlayerSrc(): string {
    // Stands in for the %%CACHEBUSTER%% macro an ad server would expand
    const cacheBuster = Math.floor(Math.random() * 1e10).toString()
    const params = new URLSearchParams({
      s: props.siteId,
      cbuster: cacheBuster,
      playerApiId: props.playerApiId,
    })

    return `${PRIMIS_LIVE_VIEW_URL}?${params.toString()}`
  }

  let stopWatchingVisibility = () => {}

  function renderPrimisPlayer() {
    if (!props.playerApiId) {
      logger.error('[renderPrimisPlayer] Missing playerApiId', {
        placement: props.placement,
      })
      return
    }

    const wrapper = wrapperEl.value
    const host = playerHostEl.value
    if (!wrapper || !host || wrapper.getAttribute('data-render-registered')) {
      return
    }

    const index = getIndexOfPlacement(props.placement, wrapper)
    placementClassName.value =
      index > 0
        ? `${props.placement} ${props.placement}-${index}`
        : props.placement

    wrapper.setAttribute('data-render-registered', 'true')

    // Must be a real script element — innerHTML would never execute it
    scriptEl = document.createElement('script')
    scriptEl.type = 'text/javascript'
    scriptEl.src = buildPlayerSrc()
    scriptEl.addEventListener('load', () => {
      track('NtpAd_Rendered', getEventData())
      // The tag loading is not the player showing. The host counts once the
      // player has laid itself out in it and is on screen.
      stopWatchingVisibility = watchAdVisibility(
        playerHostEl.value,
        'primis',
        props.placement
      )
      logger.debug('[renderPrimisPlayer] Player tag loaded', {
        placement: props.placement,
        playerApiId: props.playerApiId,
      })
    })
    scriptEl.addEventListener('error', () => {
      const errorEventData = {
        ...getEventData(),
        ntp_error_message: 'Failed to load Primis liveView script',
      }

      track('NtpAd_Render_Failed', errorEventData)
      logger.error('[renderPrimisPlayer] Player tag failed to load', {
        placement: props.placement,
        playerApiId: props.playerApiId,
      })
    })

    markFirstAdRequest('primis')
    host.appendChild(scriptEl)
  }

  onMounted(() => {
    renderWhenCurrentElementIsVisible(
      wrapperEl.value,
      props.lazyRender,
      props.sticky,
      renderPrimisPlayer
    )
  })

  onBeforeUnmount(() => {
    stopWatchingVisibility()
    // No vendor teardown API, so drop the subtree the tag built
    scriptEl?.remove()
    scriptEl = null
    playerHostEl.value?.replaceChildren()
    wrapperEl.value?.removeAttribute('data-render-registered')
  })

  defineExpose({
    renderPrimisPlayer,
    buildPlayerSrc,
  })
</script>

<template>
  <div
    ref="wrapperEl"
    class="flex h-full w-full items-center justify-center"
    :class="placementClassName"
    :data-ad-instance-id="adInstanceId"
    data-ad-provider="primis"
  >
    <div
      ref="playerHostEl"
      class="w-full"
      :style="{ maxWidth: playerMaxWidth }"
    />
  </div>
</template>
